import React, { useEffect, useState } from "react";
import {
  HiCheckCircle,
  HiCreditCard,
  HiSparkles,
} from "react-icons/hi2";
import apiClient from "../api/apiClient";
import LoadingSpinner from "../components/LoadingSpinner";
import { toast } from "react-toastify";
import { handleErrors } from "../modules/handleErrors";
import { useDispatch } from "react-redux";
import { useIndexPageData } from "../contextapi/Indexpagedata";
import { updatelogin } from "../redux/authSlice";
import SubscriptionModal from "../modals/StripeSubscriptionModal";
import StripeCountrySelectModal from "../modals/StripeCountrySelectModal";

const getSubscriptionStatus = (profiledata) => {
  if (!profiledata?.is_seller_pro_active) {
    return {
      active: false,
      title: "No active subscription",
      description: (
        <>
          Unlock Seller Pro features for only{" "}
          <strong style={{ color: "#111" }}>$9.99 / month</strong>
        </>
      ),
      badge: "Not subscribed",
      badgeVariant: "inactive",
    };
  }

  const end = new Date(profiledata.subscription_end_date);
  const today = new Date();
  const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));

  if (diff > 0) {
    return {
      active: true,
      title: "Your subscription is active",
      description: `You have ${diff} day${diff > 1 ? "s" : ""} left — renews on ${end.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}.`,
      badge: "Active",
      badgeVariant: "active",
    };
  }

  return {
    active: false,
    title: "Subscription expired",
    description: "Your Seller Pro plan has ended. Renew to keep premium features.",
    badge: "Expired",
    badgeVariant: "warning",
  };
};

const STRIPE_FEATURES = [
  "Receive payouts from mockup sales",
  "Secure, PCI-compliant payment processing",
  "Manage payouts from your Stripe dashboard",
];

const PRO_FEATURES = [
  "Get shareable product links for your mockups and sell them directly on external platforms — no Mockee commission deducted."
];

const Payments = () => {
  const { profiledata, fetchDataprofile } = useIndexPageData();
  const [loader, setloader] = useState(false);
  const [Loader, setLoader] = useState(false);
  const [loaderdata, setloaderdata] = useState(false);
  const [contectid, setcontectid] = useState(false);
  const [timeoutmanges, settimeoutmanges] = useState(2000);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);

  const getCurrentURLParameters = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const params = {};
    for (const [key, value] of searchParams) {
      params[key] = value;
    }
    return params;
  };
  const { id, user } = getCurrentURLParameters();

  const dispatch = useDispatch();

  const postuserandid = async () => {
    try {
      const result = await apiClient.get(
        `/StripeAccountViewSet/activate_account/?user=${user}&id=${id}`,
        {}
      );

      fetchDataprofile();

      if (profiledata?.role) {
        dispatch(updatelogin({ userType: profiledata?.role }));
      }

      if (!result.ok) return handleErrors(result.data);
      toast.success("Account Created  successfully");
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchconect = async (countrycode) => {
    setLoader(true);
    const result = await apiClient.get(
      `/StripeAccountViewSet/create_stripe_account/?country_code=${encodeURIComponent(countrycode)}`
    );
    setLoader(false);
    fetchdata();
    if (!result.ok) return handleErrors(result.data);
    setShowCountryModal(false);
    settimeoutmanges(99999999979900000);
    window.location.href = result?.data?.account_link;
  };

  const fetchManage = async () => {
    setloader(true);
    const result = await apiClient.get(
      "/StripeAccountViewSet/create_login_link/"
    );
    setloader(false);
    fetchdata();
    if (!result.ok) return toast.error("please try again");

    window.open(result?.data?.account_link, "_blank");
  };

  const fetchdata = async () => {
    setloaderdata(true);
    const result = await apiClient.get("/StripeConnectViewSet/");
    setloaderdata(false);
    setcontectid(result?.data?.connect_id);

    if (!result.ok) return;
  };

  const handleUnSubscribe = async () => {
    try {
      const res = await apiClient.delete("/subscription/abc/");
      if (!res.ok) return handleErrors(res?.data);
      fetchDataprofile();

      toast.success("Unsubscribed successfully");
    } catch (error) {
      // silent
    }
  };

  useEffect(() => {
    fetchdata();
    fetchDataprofile();
  }, []);

  useEffect(() => {
    if (id && user) {
      postuserandid();
    }
  }, [user, id]);

  useEffect(() => {
    if (contectid && timeoutmanges !== 2000) {
      setTimeout(() => {
        fetchManage();
      }, timeoutmanges);
    }
  }, [contectid]);

  const subscription = getSubscriptionStatus(profiledata);
  const isSubscriptionActive =
    profiledata?.is_seller_pro_active &&
    new Date(profiledata?.subscription_end_date) > new Date();

  if (loaderdata) {
    return (
      <div className="d-flex justify-content-center mt-5 py-5">
        <LoadingSpinner color="#FF3E1F" />
      </div>
    );
  }

  return (
    <div className="seller-dashboard px-md-3 px-2 pb-4">
      <div className="container py-3 px-3 px-md-4">
        <section className="seller-dashboard__hero mb-4">
          <p className="seller-dashboard__eyebrow mb-2">Seller Hub</p>
          <h1 className="seller-dashboard__title mb-2">Billing Information</h1>
          <p className="seller-dashboard__subtitle mb-0">
            Connect your Stripe account to receive payouts and manage your Seller
            Pro subscription in one place.
          </p>
        </section>

        <section className="seller-dashboard__actions-grid">
          <article className="seller-dashboard__panel seller-dashboard__billing-card">
            <div className="seller-dashboard__panel-header">
              <div className="seller-dashboard__billing-stripe-icon" aria-hidden="true">
                <svg viewBox="0 0 60 25" width="36" height="15" fill="#635BFF">
                  <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.98 2.82-7.48 6.65-7.48 3.87 0 5.55 2.86 5.55 6.28 0 .61-.06 1.22-.1 1.66zm-5.03-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 4.63-2.7 7.6-5.65 7.6zM40 8.95c-.81 0-1.37.28-1.75.65l.02 6.12c.38.36.94.64 1.73.64 1.33 0 2.25-1.46 2.25-3.87 0-2.3-.93-3.54-2.25-3.54zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.87zm-6.88 4.7h3.77l.08 1.02c.87-.8 2.04-1.28 3.25-1.28 3.07 0 4.74 2.48 4.74 6.12 0 3.87-1.8 6.32-4.83 6.32-1.15 0-2.18-.4-2.87-1.07l-.02 5.05h-4.12V5.57zm3.57 8.34c.7 0 1.27-.24 1.64-.62V9.5c-.36-.4-.93-.66-1.6-.66-1.36 0-2.28 1.5-2.28 3.64 0 2.05.84 3.43 2.24 3.43zM9.02 5.57H13l-4.1 14.44H4.9L.8 5.57h3.98l2.12 8.08 2.12-8.08z" />
                </svg>
              </div>
              <div className="flex-grow-1 min-w-0">
                <div className="seller-dashboard__billing-title-row">
                  <h2 className="seller-dashboard__panel-title mb-0">
                    Stripe Connect
                  </h2>
                  <span
                    className={`seller-dashboard__badge seller-dashboard__badge--${
                      contectid ? "active" : "inactive"
                    }`}
                  >
                    {contectid ? "Connected" : "Not connected"}
                  </span>
                </div>
                <p className="seller-dashboard__panel-text mb-0">
                  Create and link your Stripe account to receive payments from your mockup
                  sales securely.
                </p>
              </div>
            </div>

            <ul className="seller-dashboard__billing-features list-unstyled mb-0">
              {STRIPE_FEATURES.map((feature) => (
                <li key={feature} className="seller-dashboard__billing-feature">
                  <HiCheckCircle size={16} aria-hidden="true" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="seller-dashboard__billing-actions">
              {contectid ? (
                <button
                  type="button"
                  className="seller-dashboard__btn seller-dashboard__btn--primary w-100 w-sm-auto"
                  disabled={loader}
                  onClick={fetchManage}
                >
                  {loader ? "Opening dashboard..." : "Manage Stripe account"}
                </button>
              ) : (
                <button
                  type="button"
                  className="seller-dashboard__btn seller-dashboard__btn--primary w-100 w-sm-auto"
                  disabled={Loader}
                  onClick={() => setShowCountryModal(true)}
                >
                  {Loader ? "Connecting..." : "Connect with Stripe"}
                </button>
              )}
            </div>
          </article>

          <article className="seller-dashboard__panel seller-dashboard__billing-card seller-dashboard__billing-card--pro">
            <div className="seller-dashboard__panel-header">
              <div className="seller-dashboard__panel-icon">
                <HiSparkles size={22} />
              </div>
              <div className="flex-grow-1 min-w-0">
                <div className="seller-dashboard__billing-title-row">
                  <h2 className="seller-dashboard__panel-title mb-0">
                    Seller Pro
                  </h2>
                  <span
                    className={`seller-dashboard__badge seller-dashboard__badge--${subscription.badgeVariant}`}
                  >
                    {subscription.badge}
                  </span>
                </div>
                <p className="seller-dashboard__panel-text mb-0">
                  {subscription.title}
                </p>
              </div>
            </div>

            <p className="seller-dashboard__billing-status-text mb-0">
              {subscription.description}
            </p>

            <ul className="seller-dashboard__billing-features list-unstyled mb-0">
              {PRO_FEATURES.map((feature) => (
                <li key={feature} className="seller-dashboard__billing-feature">
                  <HiCheckCircle size={16} aria-hidden="true" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="seller-dashboard__billing-actions">
              {isSubscriptionActive ? (
                <button
                  type="button"
                  className="seller-dashboard__btn seller-dashboard__btn--danger w-100 w-sm-auto"
                  onClick={handleUnSubscribe}
                >
                  Unsubscribe
                </button>
              ) : (
                <button
                  type="button"
                  className="seller-dashboard__btn seller-dashboard__btn--primary w-100 w-sm-auto"
                  onClick={() => setShowSubscriptionModal(true)}
                >
                  Subscribe for $9.99/mo
                </button>
              )}
            </div>
          </article>
        </section>

        <section className="seller-dashboard__panel seller-dashboard__billing-info mt-4">
          <div className="seller-dashboard__panel-header mb-0">
            <div className="seller-dashboard__panel-icon">
              <HiCreditCard size={22} />
            </div>
            <div>
              <h2 className="seller-dashboard__panel-title mb-1">
                Need help with billing?
              </h2>
              <p className="seller-dashboard__panel-text mb-0">
                Stripe handles all payment processing and payout transfers. For
                subscription changes, use the controls above — your plan updates
                take effect immediately.
              </p>
            </div>
          </div>
        </section>
      </div>

      <SubscriptionModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onSubscribed={fetchDataprofile}
      />

      <StripeCountrySelectModal
        open={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        onConfirm={fetchconect}
        loading={Loader}
      />
    </div>
  );
};

export default Payments;
